/**
 * Plant Loop Component Definitions
 *
 * Pre-built definitions for plant equipment components used in the visual editor.
 * Each definition includes ports, IDF mapping, and default values.
 */

import type { PlantComponentDefinition, PlantComponentCategory, PlantLoopType } from './types';

/**
 * All plant loop component definitions
 */
export const PLANT_COMPONENTS: PlantComponentDefinition[] = [
  // ============================================
  // CHILLERS
  // ============================================
  {
    type: 'chiller-electric-eir',
    displayName: 'Electric Chiller (EIR)',
    category: 'chillers',
    icon: 'chiller',
    idfClass: 'Chiller:Electric:EIR',
    description: 'Electric centrifugal or screw chiller using EIR model',
    color: '#1E88E5',
    allowedLoopTypes: ['chilled-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'chw-inlet',
        name: 'CHW Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.25,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'chw-outlet',
        name: 'CHW Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.25,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'cw-inlet',
        name: 'Condenser Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.75,
        required: true,
        loopSide: 'demand',
      },
      {
        id: 'cw-outlet',
        name: 'Condenser Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.75,
        required: true,
        loopSide: 'demand',
      },
    ],
    defaults: {
      'Reference Capacity': 'autosize',
      'Reference COP': '5.5',
      'Reference Leaving Chilled Water Temperature': '6.67',
      'Reference Entering Condenser Fluid Temperature': '29.4',
      'Reference Chilled Water Flow Rate': 'autosize',
      'Reference Condenser Fluid Flow Rate': 'autosize',
      'Condenser Type': 'WaterCooled',
    },
  },
  {
    type: 'chiller-electric-reformulated-eir',
    displayName: 'Electric Chiller (Reformulated EIR)',
    category: 'chillers',
    icon: 'chiller',
    idfClass: 'Chiller:Electric:ReformulatedEIR',
    description: 'Electric chiller using reformulated EIR model with improved part-load curves',
    color: '#1E88E5',
    allowedLoopTypes: ['chilled-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'chw-inlet',
        name: 'CHW Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.25,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'chw-outlet',
        name: 'CHW Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.25,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'cw-inlet',
        name: 'Condenser Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.75,
        required: true,
        loopSide: 'demand',
      },
      {
        id: 'cw-outlet',
        name: 'Condenser Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.75,
        required: true,
        loopSide: 'demand',
      },
    ],
    defaults: {
      'Reference Capacity': 'autosize',
      'Reference COP': '5.5',
      'Reference Leaving Chilled Water Temperature': '6.67',
      'Reference Leaving Condenser Water Temperature': '35.0',
      'Reference Chilled Water Flow Rate': 'autosize',
      'Reference Condenser Water Flow Rate': 'autosize',
    },
  },
  {
    type: 'chiller-absorption',
    displayName: 'Absorption Chiller',
    category: 'chillers',
    icon: 'chiller',
    idfClass: 'Chiller:Absorption',
    description: 'Single-effect absorption chiller',
    color: '#1565C0',
    allowedLoopTypes: ['chilled-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'chw-inlet',
        name: 'CHW Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.2,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'chw-outlet',
        name: 'CHW Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.2,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'cw-inlet',
        name: 'Condenser Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'demand',
      },
      {
        id: 'cw-outlet',
        name: 'Condenser Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'demand',
      },
      {
        id: 'hw-inlet',
        name: 'Generator HW Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.8,
        required: false,
        loopSide: 'demand',
      },
      {
        id: 'hw-outlet',
        name: 'Generator HW Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.8,
        required: false,
        loopSide: 'demand',
      },
    ],
    defaults: {
      'Nominal Capacity': 'autosize',
      'Nominal Pumping Power': 'autosize',
      'Design Chilled Water Flow Rate': 'autosize',
      'Design Condenser Water Flow Rate': 'autosize',
      'Coefficient of Performance': '0.7',
    },
  },
  {
    type: 'chiller-absorption-indirect',
    displayName: 'Indirect Absorption Chiller',
    category: 'chillers',
    icon: 'chiller',
    idfClass: 'Chiller:Absorption:Indirect',
    description: 'Indirect-fired absorption chiller',
    color: '#1565C0',
    allowedLoopTypes: ['chilled-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'chw-inlet',
        name: 'CHW Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.25,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'chw-outlet',
        name: 'CHW Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.25,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'cw-inlet',
        name: 'Condenser Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.75,
        required: true,
        loopSide: 'demand',
      },
      {
        id: 'cw-outlet',
        name: 'Condenser Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.75,
        required: true,
        loopSide: 'demand',
      },
    ],
    defaults: {
      'Nominal Capacity': 'autosize',
      'Nominal Pumping Power': 'autosize',
      'Design Chilled Water Flow Rate': 'autosize',
      'Design Condenser Water Flow Rate': 'autosize',
    },
  },

  // ============================================
  // BOILERS
  // ============================================
  {
    type: 'boiler-hot-water',
    displayName: 'Hot Water Boiler',
    category: 'boilers',
    icon: 'boiler',
    idfClass: 'Boiler:HotWater',
    description: 'Gas-fired or electric hot water boiler',
    color: '#E53935',
    allowedLoopTypes: ['hot-water', 'service-hot-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Fuel Type': 'NaturalGas',
      'Nominal Capacity': 'autosize',
      'Nominal Thermal Efficiency': '0.80',
      'Efficiency Curve Temperature Evaluation Variable': 'LeavingBoiler',
      'Design Water Flow Rate': 'autosize',
      'Minimum Part Load Ratio': '0.0',
      'Maximum Part Load Ratio': '1.1',
      'Boiler Flow Mode': 'NotModulated',
    },
  },
  {
    type: 'boiler-steam',
    displayName: 'Steam Boiler',
    category: 'boilers',
    icon: 'boiler',
    idfClass: 'Boiler:Steam',
    description: 'Steam boiler for steam heating systems',
    color: '#C62828',
    allowedLoopTypes: ['hot-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Steam Outlet',
        direction: 'outlet',
        medium: 'steam',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Fuel Type': 'NaturalGas',
      'Maximum Operating Pressure': '160000',
      'Nominal Capacity': 'autosize',
      'Theoretical Efficiency': '0.80',
    },
  },

  // ============================================
  // COOLING TOWERS
  // ============================================
  {
    type: 'cooling-tower-single-speed',
    displayName: 'Cooling Tower (Single Speed)',
    category: 'cooling-towers',
    icon: 'tower',
    idfClass: 'CoolingTower:SingleSpeed',
    description: 'Single-speed cooling tower with constant fan',
    color: '#8E24AA',
    allowedLoopTypes: ['condenser-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Design Water Flow Rate': 'autosize',
      'Design Air Flow Rate': 'autosize',
      'Design Fan Power': 'autosize',
      'Design U-Factor Times Area Value': 'autosize',
      'Performance Input Method': 'UFactorTimesAreaAndDesignWaterFlowRate',
    },
  },
  {
    type: 'cooling-tower-two-speed',
    displayName: 'Cooling Tower (Two Speed)',
    category: 'cooling-towers',
    icon: 'tower',
    idfClass: 'CoolingTower:TwoSpeed',
    description: 'Two-speed cooling tower with high/low fan speeds',
    color: '#8E24AA',
    allowedLoopTypes: ['condenser-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Design Water Flow Rate': 'autosize',
      'High Fan Speed Air Flow Rate': 'autosize',
      'High Fan Speed Fan Power': 'autosize',
      'Low Fan Speed Air Flow Rate': 'autosize',
      'Low Fan Speed Fan Power': 'autosize',
      'Performance Input Method': 'UFactorTimesAreaAndDesignWaterFlowRate',
    },
  },
  {
    type: 'cooling-tower-variable-speed',
    displayName: 'Cooling Tower (Variable Speed)',
    category: 'cooling-towers',
    icon: 'tower',
    idfClass: 'CoolingTower:VariableSpeed',
    description: 'Variable-speed cooling tower with VFD fan control',
    color: '#7B1FA2',
    allowedLoopTypes: ['condenser-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Design Water Flow Rate': 'autosize',
      'Design Air Flow Rate': 'autosize',
      'Design Fan Power': 'autosize',
      'Model Type': 'YorkCalc',
    },
  },
  {
    type: 'fluid-cooler-single-speed',
    displayName: 'Fluid Cooler (Single Speed)',
    category: 'cooling-towers',
    icon: 'fluid-cooler',
    idfClass: 'FluidCooler:SingleSpeed',
    description: 'Closed-circuit fluid cooler with single-speed fan',
    color: '#6A1B9A',
    allowedLoopTypes: ['condenser-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Design Water Flow Rate': 'autosize',
      'Design Air Flow Rate': 'autosize',
      'Design Air Flow Rate Fan Power': 'autosize',
      'Performance Input Method': 'UFactorTimesAreaAndDesignWaterFlowRate',
    },
  },

  // ============================================
  // HEAT EXCHANGERS
  // ============================================
  {
    type: 'heat-exchanger-fluid-to-fluid',
    displayName: 'Fluid-to-Fluid Heat Exchanger',
    category: 'heat-exchangers',
    icon: 'heat-exchanger',
    idfClass: 'HeatExchanger:FluidToFluid',
    description: 'Plate or shell-and-tube heat exchanger between two loops',
    color: '#00897B',
    allowedLoopTypes: ['chilled-water', 'hot-water', 'condenser-water', 'ground-loop'],
    supplyEquipment: true,
    demandEquipment: true,
    ports: [
      {
        id: 'loop-supply-inlet',
        name: 'Loop Supply Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.25,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'loop-supply-outlet',
        name: 'Loop Supply Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.25,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'loop-demand-inlet',
        name: 'Loop Demand Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.75,
        required: true,
        loopSide: 'demand',
      },
      {
        id: 'loop-demand-outlet',
        name: 'Loop Demand Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.75,
        required: true,
        loopSide: 'demand',
      },
    ],
    defaults: {
      'Heat Exchange Model Type': 'Ideal',
      'Loop Demand Side Design Flow Rate': 'autosize',
      'Loop Supply Side Design Flow Rate': 'autosize',
      'Control Type': 'UncontrolledOn',
    },
  },
  {
    type: 'ground-heat-exchanger-system',
    displayName: 'Ground Heat Exchanger',
    category: 'heat-exchangers',
    icon: 'ground-loop',
    idfClass: 'GroundHeatExchanger:System',
    description: 'Vertical bore ground heat exchanger system',
    color: '#43A047',
    allowedLoopTypes: ['ground-loop'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Design Flow Rate': 'autosize',
      'Number of Bore Holes': '100',
      'Bore Hole Length': '76.2',
      'Bore Hole Radius': '0.0635',
      'Ground Thermal Conductivity': '2.493',
      'Ground Thermal Heat Capacity': '2.5E+06',
    },
  },
  {
    type: 'ground-heat-exchanger-horizontal-trench',
    displayName: 'Horizontal Ground HX',
    category: 'heat-exchangers',
    icon: 'ground-loop',
    idfClass: 'GroundHeatExchanger:HorizontalTrench',
    description: 'Horizontal trench ground heat exchanger',
    color: '#388E3C',
    allowedLoopTypes: ['ground-loop'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Design Flow Rate': 'autosize',
      'Trench Length in Pipe Axial Direction': '50',
      'Number of Trenches': '2',
      'Horizontal Spacing Between Pipes': '1.0',
      'Pipe Outer Diameter': '0.0267',
      'Pipe Thermal Conductivity': '0.4',
    },
  },

  // ============================================
  // PUMPS
  // ============================================
  {
    type: 'pump-variable-speed',
    displayName: 'Variable Speed Pump',
    category: 'pumps',
    icon: 'pump',
    idfClass: 'Pump:VariableSpeed',
    description: 'Variable speed water pump with VFD',
    color: '#3F51B5',
    allowedLoopTypes: ['chilled-water', 'hot-water', 'condenser-water', 'ground-loop', 'service-hot-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Design Maximum Flow Rate': 'autosize',
      'Design Pump Head': '179352',
      'Design Power Consumption': 'autosize',
      'Motor Efficiency': '0.9',
      'Fraction of Motor Inefficiencies to Fluid Stream': '0.0',
      'Pump Control Type': 'Intermittent',
      'VFD Control Type': 'PressureSetPointControl',
    },
  },
  {
    type: 'pump-constant-speed',
    displayName: 'Constant Speed Pump',
    category: 'pumps',
    icon: 'pump',
    idfClass: 'Pump:ConstantSpeed',
    description: 'Constant speed water pump',
    color: '#3949AB',
    allowedLoopTypes: ['chilled-water', 'hot-water', 'condenser-water', 'ground-loop', 'service-hot-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Design Flow Rate': 'autosize',
      'Design Pump Head': '179352',
      'Design Power Consumption': 'autosize',
      'Motor Efficiency': '0.9',
      'Fraction of Motor Inefficiencies to Fluid Stream': '0.0',
      'Pump Control Type': 'Intermittent',
    },
  },
  {
    type: 'headered-pumps-variable-speed',
    displayName: 'Headered Pumps (Variable)',
    category: 'pumps',
    icon: 'pump-headered',
    idfClass: 'HeaderedPumps:VariableSpeed',
    description: 'Multiple parallel pumps with variable speed drives',
    color: '#303F9F',
    allowedLoopTypes: ['chilled-water', 'hot-water', 'condenser-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Total Design Flow Rate': 'autosize',
      'Number of Pumps in Bank': '2',
      'Design Pump Head': '179352',
      'Design Power Consumption': 'autosize',
      'Motor Efficiency': '0.9',
      'Flow Sequencing Control Scheme': 'Sequential',
    },
  },
  {
    type: 'headered-pumps-constant-speed',
    displayName: 'Headered Pumps (Constant)',
    category: 'pumps',
    icon: 'pump-headered',
    idfClass: 'HeaderedPumps:ConstantSpeed',
    description: 'Multiple parallel pumps with constant speed',
    color: '#303F9F',
    allowedLoopTypes: ['chilled-water', 'hot-water', 'condenser-water'],
    supplyEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'water-outlet',
        name: 'Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'supply',
      },
    ],
    defaults: {
      'Total Design Flow Rate': 'autosize',
      'Number of Pumps in Bank': '2',
      'Design Pump Head': '179352',
      'Design Power Consumption': 'autosize',
      'Motor Efficiency': '0.9',
      'Flow Sequencing Control Scheme': 'Sequential',
    },
  },

  // ============================================
  // DISTRIBUTION (SPLITTERS/MIXERS/PIPES)
  // ============================================
  {
    type: 'plant-splitter',
    displayName: 'Splitter',
    category: 'distribution',
    icon: 'splitter',
    idfClass: 'Connector:Splitter',
    description: 'Splits flow to multiple parallel branches',
    color: '#795548',
    allowedLoopTypes: ['chilled-water', 'hot-water', 'condenser-water', 'ground-loop', 'service-hot-water'],
    ports: [
      {
        id: 'inlet',
        name: 'Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
      {
        id: 'outlet-1',
        name: 'Outlet 1',
        direction: 'outlet',
        medium: 'water',
        position: 0.2,
        required: true,
      },
      {
        id: 'outlet-2',
        name: 'Outlet 2',
        direction: 'outlet',
        medium: 'water',
        position: 0.4,
        required: false,
      },
      {
        id: 'outlet-3',
        name: 'Outlet 3',
        direction: 'outlet',
        medium: 'water',
        position: 0.6,
        required: false,
      },
      {
        id: 'outlet-4',
        name: 'Outlet 4',
        direction: 'outlet',
        medium: 'water',
        position: 0.8,
        required: false,
      },
    ],
    defaults: {},
  },
  {
    type: 'plant-mixer',
    displayName: 'Mixer',
    category: 'distribution',
    icon: 'mixer',
    idfClass: 'Connector:Mixer',
    description: 'Mixes flow from multiple parallel branches',
    color: '#795548',
    allowedLoopTypes: ['chilled-water', 'hot-water', 'condenser-water', 'ground-loop', 'service-hot-water'],
    ports: [
      {
        id: 'inlet-1',
        name: 'Inlet 1',
        direction: 'inlet',
        medium: 'water',
        position: 0.2,
        required: true,
      },
      {
        id: 'inlet-2',
        name: 'Inlet 2',
        direction: 'inlet',
        medium: 'water',
        position: 0.4,
        required: false,
      },
      {
        id: 'inlet-3',
        name: 'Inlet 3',
        direction: 'inlet',
        medium: 'water',
        position: 0.6,
        required: false,
      },
      {
        id: 'inlet-4',
        name: 'Inlet 4',
        direction: 'inlet',
        medium: 'water',
        position: 0.8,
        required: false,
      },
      {
        id: 'outlet',
        name: 'Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {},
  },
  {
    type: 'pipe-adiabatic',
    displayName: 'Pipe (Adiabatic)',
    category: 'distribution',
    icon: 'pipe',
    idfClass: 'Pipe:Adiabatic',
    description: 'Simple adiabatic pipe connector',
    color: '#6D4C41',
    allowedLoopTypes: ['chilled-water', 'hot-water', 'condenser-water', 'ground-loop', 'service-hot-water'],
    ports: [
      {
        id: 'inlet',
        name: 'Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
      {
        id: 'outlet',
        name: 'Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {},
  },

  // ============================================
  // COILS (DEMAND SIDE EQUIPMENT)
  // ============================================
  {
    type: 'coil-cooling-water-plant',
    displayName: 'Chilled Water Coil',
    category: 'coils',
    icon: 'coil-cooling',
    idfClass: 'Coil:Cooling:Water',
    description: 'Chilled water cooling coil (demand side connection)',
    color: '#29B6F6',
    allowedLoopTypes: ['chilled-water'],
    demandEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'demand',
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'demand',
      },
    ],
    defaults: {
      'Design Water Flow Rate': 'autosize',
      'Design Air Flow Rate': 'autosize',
      'Design Inlet Water Temperature': '7.22',
      'Design Inlet Air Temperature': '24.0',
      'Design Outlet Air Temperature': '12.8',
    },
  },
  {
    type: 'coil-heating-water-plant',
    displayName: 'Hot Water Coil',
    category: 'coils',
    icon: 'coil-heating',
    idfClass: 'Coil:Heating:Water',
    description: 'Hot water heating coil (demand side connection)',
    color: '#EF5350',
    allowedLoopTypes: ['hot-water'],
    demandEquipment: true,
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'demand',
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
        loopSide: 'demand',
      },
    ],
    defaults: {
      'U-Factor Times Area Value': 'autosize',
      'Maximum Water Flow Rate': 'autosize',
      'Rated Inlet Water Temperature': '82.2',
      'Rated Inlet Air Temperature': '16.6',
      'Rated Outlet Water Temperature': '71.1',
      'Rated Outlet Air Temperature': '32.2',
    },
  },
  {
    type: 'water-heater-mixed',
    displayName: 'Water Heater (Mixed)',
    category: 'coils',
    icon: 'water-heater',
    idfClass: 'WaterHeater:Mixed',
    description: 'Mixed storage water heater',
    color: '#FB8C00',
    allowedLoopTypes: ['service-hot-water'],
    supplyEquipment: true,
    demandEquipment: true,
    ports: [
      {
        id: 'use-inlet',
        name: 'Use Side Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.25,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'use-outlet',
        name: 'Use Side Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.25,
        required: true,
        loopSide: 'supply',
      },
      {
        id: 'source-inlet',
        name: 'Source Side Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.75,
        required: false,
        loopSide: 'demand',
      },
      {
        id: 'source-outlet',
        name: 'Source Side Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.75,
        required: false,
        loopSide: 'demand',
      },
    ],
    defaults: {
      'Tank Volume': '0.3785',
      'Setpoint Temperature Schedule Name': '',
      'Deadband Temperature Difference': '2.0',
      'Maximum Temperature Limit': '82.2',
      'Heater Control Type': 'Cycle',
      'Heater Maximum Capacity': 'autosize',
      'Heater Fuel Type': 'Electricity',
      'Heater Thermal Efficiency': '1.0',
    },
  },
];

/**
 * Component definition lookup by type
 */
export const PLANT_COMPONENT_BY_TYPE = new Map<string, PlantComponentDefinition>(
  PLANT_COMPONENTS.map((c) => [c.type, c])
);

/**
 * Get plant component definition by type
 */
export function getPlantComponentDefinition(type: string): PlantComponentDefinition | undefined {
  return PLANT_COMPONENT_BY_TYPE.get(type);
}

/**
 * Get plant components by category
 */
export function getPlantComponentsByCategory(category: PlantComponentCategory): PlantComponentDefinition[] {
  return PLANT_COMPONENTS.filter((c) => c.category === category);
}

/**
 * Get plant components by loop type
 */
export function getPlantComponentsByLoopType(loopType: PlantLoopType): PlantComponentDefinition[] {
  return PLANT_COMPONENTS.filter((c) => c.allowedLoopTypes.includes(loopType));
}

/**
 * Get supply equipment for a loop type
 */
export function getSupplyEquipment(loopType: PlantLoopType): PlantComponentDefinition[] {
  return PLANT_COMPONENTS.filter(
    (c) => c.supplyEquipment && c.allowedLoopTypes.includes(loopType)
  );
}

/**
 * Get demand equipment for a loop type
 */
export function getDemandEquipment(loopType: PlantLoopType): PlantComponentDefinition[] {
  return PLANT_COMPONENTS.filter(
    (c) => c.demandEquipment && c.allowedLoopTypes.includes(loopType)
  );
}

/**
 * Category display names
 */
export const PLANT_CATEGORY_NAMES: Record<PlantComponentCategory, string> = {
  chillers: 'Chillers',
  boilers: 'Boilers',
  'cooling-towers': 'Cooling Towers & Fluid Coolers',
  'heat-exchangers': 'Heat Exchangers',
  pumps: 'Pumps',
  distribution: 'Distribution',
  coils: 'Coils & Water Heaters',
};

/**
 * All plant categories in display order
 */
export const PLANT_CATEGORIES: PlantComponentCategory[] = [
  'chillers',
  'boilers',
  'cooling-towers',
  'heat-exchangers',
  'pumps',
  'distribution',
  'coils',
];
